import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";

const DOT_GREEN = "#4caf50";
const DOT_YELLOW = "#f5b841";
const DOT_RED = "#f44336";
const DOT_MUTED = "rgba(255, 255, 255, 0.32)";

function getDotColor(compatibleCount) {
  if (compatibleCount === null || compatibleCount === undefined) return DOT_MUTED;
  if (compatibleCount === 0) return DOT_RED;
  if (compatibleCount < 5) return DOT_YELLOW;
  return DOT_GREEN;
}

function formatPlayerLabel(count) {
  return `${count} compatible player${count === 1 ? "" : "s"} in queue`;
}

function getEstimateText({ compatibleCount, totalInQueue, loading, error }) {
  if (error) return "Queue estimate unavailable";
  if (loading && (compatibleCount === null || compatibleCount === undefined)) {
    return "Checking queue fit...";
  }
  if (compatibleCount === null || compatibleCount === undefined) {
    return "Queue estimate pending";
  }
  if (compatibleCount === 0) {
    return totalInQueue > 0
      ? "No compatible players in queue"
      : "No players currently searching";
  }
  return `~${formatPlayerLabel(compatibleCount)}`;
}

const styles = () => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.45rem",
    padding: "0.42rem 0.72rem",
    borderRadius: "10px",
    border: "1px solid rgba(232, 163, 61, 0.18)",
    backgroundColor: "rgba(28, 30, 32, 0.72)",
    backdropFilter: "blur(8px)",
    maxWidth: "320px",
    transition: "opacity 0.15s ease-in-out, border-color 0.15s ease-in-out",
  },
  compact: {
    justifyContent: "flex-start",
    maxWidth: "none",
    padding: "0.48rem 0.6rem",
    backgroundColor: "rgba(255, 255, 255, 0.035)",
    borderColor: "rgba(255, 255, 255, 0.08)",
  },
  loading: {
    opacity: 0.72,
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  text: {
    color: "rgba(255, 255, 255, 0.78) !important",
    fontSize: "0.72rem !important",
    fontWeight: "600 !important",
    lineHeight: "1.2 !important",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  compactText: {
    textAlign: "left",
    whiteSpace: "normal",
  },
  hint: {
    color: "rgba(255, 255, 255, 0.48) !important",
    fontSize: "0.66rem !important",
    fontWeight: "400 !important",
    lineHeight: "1.25 !important",
    textAlign: "center",
  },
  compactHint: {
    textAlign: "left",
  },
  textStack: {
    display: "flex",
    flexDirection: "column",
    gap: "0.12rem",
    minWidth: 0,
  },
});

function QueueEstimateIndicator({
  classes,
  estimate,
  compact = false,
  className = "",
}) {
  const safeEstimate = estimate || {};
  const {
    compatibleCount,
    totalInQueue,
    playersNeeded,
    partySize = 1,
    loading,
    error,
  } = safeEstimate;
  const text = getEstimateText({
    compatibleCount,
    totalInQueue,
    loading,
    error,
  });
  const shouldSuggestRelaxing =
    compatibleCount === 0 && totalInQueue > 0 && !error;
  const remainingNeeded =
    typeof playersNeeded === "number" && typeof compatibleCount === "number"
      ? Math.max(playersNeeded - compatibleCount - partySize, 0)
      : null;
  const hint = shouldSuggestRelaxing
    ? "Consider relaxing filters for a faster match."
    : remainingNeeded > 0 && compatibleCount > 0
      ? `${remainingNeeded} more compatible player${remainingNeeded === 1 ? "" : "s"} needed.`
      : "Estimate only; queue can change quickly.";

  return (
    <div
      className={[
        classes.root,
        compact ? classes.compact : "",
        loading ? classes.loading : "",
        className,
      ].filter(Boolean).join(" ")}
    >
      <span
        className={classes.dot}
        style={{
          backgroundColor: getDotColor(compatibleCount),
          boxShadow: `0 0 5px ${getDotColor(compatibleCount)}88`,
        }}
      />
      <div className={classes.textStack}>
        <Typography
          className={[
            classes.text,
            compact ? classes.compactText : "",
          ].filter(Boolean).join(" ")}
        >
          {text}
        </Typography>
        {!compact && (
          <Typography className={classes.hint}>
            {hint}
          </Typography>
        )}
        {compact && shouldSuggestRelaxing && (
          <Typography className={[classes.hint, classes.compactHint].join(" ")}>
            Relax filters for a faster match.
          </Typography>
        )}
      </div>
    </div>
  );
}

QueueEstimateIndicator.propTypes = {
  classes: PropTypes.object.isRequired,
  estimate: PropTypes.shape({
    compatibleCount: PropTypes.number,
    totalInQueue: PropTypes.number,
    playersNeeded: PropTypes.number,
    partySize: PropTypes.number,
    loading: PropTypes.bool,
    error: PropTypes.string,
  }),
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default withStyles(styles)(QueueEstimateIndicator);
